import { defineStore } from 'pinia';
import { getTemplateProgress } from '@/api/templates';

export interface TemplateProgress {
  id: string; // unique identifier for this operation
  type: 'edit' | 'improve' | 'create';
  templateId: string;
  versionId: string;
  progress: number; // 0-100
  status: 'pending' | 'processing' | 'completed' | 'error';
  message: string;
  startTime: number;
  endTime?: number;
  canDismiss: boolean;
  result?: Record<string, unknown>; // the response data when completed
}

interface TemplateProgressState {
  activeOperations: Record<string, TemplateProgress>;
  completedOperations: TemplateProgress[];
  showNotifications: boolean;
  pollingInterval: number | null;
}

export const useTemplateProgressStore = defineStore('templateProgress', {
  state: (): TemplateProgressState => ({
    activeOperations: {},
    completedOperations: [],
    showNotifications: true,
    pollingInterval: null
  }),

  getters: {
    hasActiveOperations: (state) =>
      Object.keys(state.activeOperations).length > 0,

    getActiveOperation: (state) => (id: string) => state.activeOperations[id],

    getAllActiveOperations: (state) => Object.values(state.activeOperations),

    getRecentCompletedOperations: (state) =>
      state.completedOperations
        .filter((op) => op.endTime && Date.now() - op.endTime < 30000) // last 30 seconds
        .slice(-3), // last 3 operations

    shouldShowProgressModal: (state) =>
      Object.keys(state.activeOperations).length > 0
  },

  actions: {
    startOperation(
      type: 'edit' | 'improve',
      templateId: string,
      versionId: string,
      message = '',
      projectId?: string
    ): string {
      const id = `${type}-${templateId}-${versionId}-${Date.now()}`;

      console.log('🟢 Starting progress operation:', {
        id,
        type,
        templateId,
        versionId,
        message,
        projectId
      });

      this.activeOperations[id] = {
        id,
        type,
        templateId,
        versionId,
        progress: 0,
        status: 'pending',
        message:
          message ||
          (type === 'edit'
            ? 'Starting template edit...'
            : 'Starting template improvement...'),
        startTime: Date.now(),
        canDismiss: false
      };

      // Start polling if we have a project ID
      if (projectId && !this.pollingInterval) {
        console.log('🔄 Starting polling for project:', projectId);
        this.startPolling(projectId);
      }

      // Start progress simulation for immediate visual feedback
      this.simulateProgress(id);

      console.log(
        '📊 Active operations count:',
        Object.keys(this.activeOperations).length
      );
      console.log('📋 All active operations:', this.activeOperations);

      return id;
    },

    // Simulate progress for immediate visual feedback
    simulateProgress(id: string) {
      const operation = this.activeOperations[id];
      if (!operation) return;

      // Start with some initial progress
      setTimeout(() => {
        this.updateProgress(id, 10, 'Processing...', 'processing');
      }, 500);

      // Continue with gradual progress
      setTimeout(() => {
        this.updateProgress(id, 25, 'Analyzing content...', 'processing');
      }, 2000);

      setTimeout(() => {
        this.updateProgress(id, 50, 'Generating improvements...', 'processing');
      }, 4000);

      setTimeout(() => {
        this.updateProgress(id, 75, 'Finalizing changes...', 'processing');
      }, 6000);
    },

    updateProgress(
      id: string,
      progress: number,
      message?: string,
      status?: 'pending' | 'processing' | 'completed' | 'error'
    ) {
      const operation = this.activeOperations[id];
      if (!operation) return;

      operation.progress = Math.min(100, Math.max(0, progress));
      if (message) operation.message = message;
      if (status) operation.status = status;

      // Allow dismissing when processing starts
      if (progress > 0 && !operation.canDismiss) {
        operation.canDismiss = true;
      }

      // Auto-complete when progress reaches 100
      if (progress >= 100 && status !== 'completed') {
        operation.status = 'completed';
        operation.endTime = Date.now();
      }
    },

    updateOperationId(oldId: string, newId: string) {
      const operation = this.activeOperations[oldId];
      if (!operation) return;

      // Update the operation with new ID
      operation.id = newId;
      this.activeOperations[newId] = operation;
      delete this.activeOperations[oldId];
    },
    // Update version id (e.g., when improvement creates a new version)
    setOperationVersion(id: string, versionId: string) {
      if (this.activeOperations[id]) {
        this.activeOperations[id].versionId = versionId;
      } else {
        const comp = this.completedOperations.find((o) => o.id === id);
        if (comp) comp.versionId = versionId;
      }
    },

    completeOperation(
      id: string,
      result?: Record<string, unknown>,
      message?: string
    ) {
      const operation = this.activeOperations[id];
      if (!operation) return;

      operation.progress = 100;
      operation.status = 'completed';
      operation.endTime = Date.now();
      operation.canDismiss = true;
      if (result) operation.result = result;
      if (message) operation.message = message;

      // Move to completed operations after a delay
      setTimeout(() => {
        this.moveToCompleted(id);
      }, 2000);
    },

    errorOperation(id: string, message: string) {
      const operation = this.activeOperations[id];
      if (!operation) return;

      operation.status = 'error';
      operation.message = message;
      operation.endTime = Date.now();
      operation.canDismiss = true;

      // Move to completed operations after a delay
      setTimeout(() => {
        this.moveToCompleted(id);
      }, 5000);
    },

    dismissOperation(id: string) {
      const operation = this.activeOperations[id];
      if (!operation || !operation.canDismiss) return;

      if (operation.status === 'completed' || operation.status === 'error') {
        this.moveToCompleted(id);
      } else {
        // For active operations, just mark as dismissible but keep running
        operation.canDismiss = true;
      }
    },

    moveToCompleted(id: string) {
      const operation = this.activeOperations[id];
      if (!operation) return;

      this.completedOperations.push(operation);
      delete this.activeOperations[id];

      // Keep only last 10 completed operations
      if (this.completedOperations.length > 10) {
        this.completedOperations = this.completedOperations.slice(-10);
      }
    },

    // Restore progress from localStorage or backend
    restoreOperation(operation: TemplateProgress) {
      this.activeOperations[operation.id] = {
        ...operation,
        canDismiss: true // Always allow dismissing restored operations
      };
    },

    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
    },

    hideNotifications() {
      this.showNotifications = false;
    },

    showNotificationsAgain() {
      this.showNotifications = true;
    },

    clearAllCompleted() {
      this.completedOperations = [];
    },

    // Start polling for progress updates
    startPolling(projectId: string) {
      if (this.pollingInterval) return; // Already polling

      this.pollingInterval = setInterval(async () => {
        if (Object.keys(this.activeOperations).length === 0) {
          this.stopPolling();
          return;
        }

        try {
          const result = await getTemplateProgress(projectId);
          if (result?.progress) {
            // Update local operations with server progress
            result.progress.forEach((serverOp) => {
              const localOp = this.activeOperations[serverOp.id];
              if (localOp) {
                this.updateProgress(
                  serverOp.id,
                  serverOp.progress,
                  serverOp.message,
                  serverOp.status
                );

                if (serverOp.status === 'completed') {
                  this.completeOperation(
                    serverOp.id,
                    undefined,
                    serverOp.message
                  );
                } else if (serverOp.status === 'error') {
                  this.errorOperation(serverOp.id, serverOp.message);
                }
              }
            });
          }
        } catch (error) {
          console.error('Error polling for progress:', error);
        }
      }, 2000); // Poll every 2 seconds
    },

    // Stop polling
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    // Request to open a specific template/version for editing (broadcast event)
    requestOpenTemplate(templateId: string, versionId?: string) {
      console.log('[templateProgressStore] requestOpenTemplate:', {
        templateId,
        versionId
      });
      const event = new CustomEvent('openTemplateEdit', {
        detail: { templateId, versionId }
      });
      window.dispatchEvent(event);
    }
  }
});
